import { useEffect } from 'react'

import { useSetState } from '@domain-group/react-hooks'
import type { UseFormPayload } from '@domain-group/react-hooks'

import useProcessingFee, { useProcessingFeeForm } from '@hooks/useProcessingFee'
import useSelector from '@hooks/useSelector'

import { EntitiesState } from '@redux/entities'

import { ProcessingFeeModel } from '@models/types'

const entityNameStrings = {
  Agency: 'agencies',
  Brand: 'brands',
  Provider: 'providers',
}

type State = {
  serviceId?: number,
  showProductSelect: boolean,
}

type SetState = (state: Partial<State>) => void

const setServiceId = (value: string, setState: SetState, setEntityState: UseFormPayload['setEntityState']) => {
  const id = Number(value)

  setState({ serviceId: id })
  setEntityState({ billable_id: id, billable_type: 'Service' })
}

const hideProductSelector = (
  serviceId: number | undefined,
  setState: SetState,
  setEntityState: UseFormPayload['setEntityState'],
) => {
  setState({ showProductSelect: false })
  setEntityState({ billable_id: serviceId, billable_type: 'Service' })
}

const setupState = (
  initProcessingFee: Partial<ProcessingFeeModel>,
  serviceProducts: EntitiesState['serviceProducts'],
  setState: SetState,
) => {
  const { billable_id, billable_type } = initProcessingFee || {}

  if (billable_type === 'ServiceProduct' && billable_id){
    const product = serviceProducts[billable_id] || {}
    setState({ serviceId: product.service_id, showProductSelect: true })
    return
  }

  setState({ serviceId: initProcessingFee.billable_id })
}

const defaultState: State = {
  serviceId: undefined,
  showProductSelect: false,
}

const useCreateOrEditProcessingFeeModal = (initProcessingFee = {}) => {
  const [state, setState] = useSetState(defaultState)
  const { serviceId, showProductSelect } = state

  const serviceProducts = useSelector(reduxState => reduxState.entities.serviceProducts)

  const {
    callbacks,
    creating,
    deleting,
    processingFee,
  } = useProcessingFee(initProcessingFee)

  const formPayload = useProcessingFeeForm(processingFee, { validateOn: 'change' })

  const { entityState, saveEnabled: formSaveEnabled, setEntityState } = formPayload
  const { global, debtor_id } = entityState

  const saveEnabled = global ? formSaveEnabled : formSaveEnabled && !!debtor_id

  // Setup initial state with service ID
  useEffect(() => setupState(initProcessingFee, serviceProducts, setState), [])

  // If Global is true, debtor_id should not be set
  useEffect(() => {
    if (global && debtor_id) setEntityState({ debtor_id: undefined })
  }, [global])

  return (
    {
      callbacks: {
        ...callbacks,
        hideProductSelector: () => hideProductSelector(serviceId, setState, setEntityState),
        showProductSelector: () => setState({ showProductSelect: true }),
        setServiceId: (value: string) => setServiceId(value, setState, setEntityState),
      },
      creating,
      deleting,
      entityNameStrings,
      formPayload: { ...formPayload, saveEnabled },
      serviceId,
      showProductSelect,
    }
  )
}

export default useCreateOrEditProcessingFeeModal
